import { RefObject, useEffect, useMemo, useRef } from 'react';
import { createDragTicker, hapGrab, hapSettle } from './haptics';

const PAGE_PX = 170;
const SPRING_K = 200;
const SPRING_DAMP = 26;
const FLING_SCALE = 0.16;

export type CarouselDriverHandle = {
  begin: () => void;
  move: (dxFromStart: number) => void;
  end: (velocityPxPerSec: number) => void;
  nudge: (dir: 1 | -1) => void;
  playHint: () => void;
  cancelHint: () => void;
  centeredIndex: () => number;
  hasUserRotated: () => boolean;
};

export type UseCarouselDriverParams = {
  count: number;
  cardRefs: RefObject<Array<HTMLDivElement | null>>;
  /** Radius in px for the current orb size — read lazily so resizes are honored. */
  getRadiusPx: () => number;
  onPageChange: (index: number) => void;
  onYawChange: (yawRadians: number) => void;
  reducedMotion: boolean;
};

/**
 * A critically-damped spring over a circular index space, one page per card.
 * Card transforms are written straight to the DOM each frame, never via React
 * state — only page-change and yaw come back through callbacks.
 */
export function useCarouselDriver({
  count,
  cardRefs,
  getRadiusPx,
  onPageChange,
  onYawChange,
  reducedMotion,
}: UseCarouselDriverParams): CarouselDriverHandle {
  const stateRef = useRef({
    pos: 0,
    target: 0,
    vel: 0,
    dragStart: 0,
    dragging: false,
    userRotated: false,
    raf: null as number | null,
    hintRaf: null as number | null,
    last: null as number | null,
  });
  const onPageChangeRef = useRef(onPageChange);
  const onYawChangeRef = useRef(onYawChange);
  onPageChangeRef.current = onPageChange;
  onYawChangeRef.current = onYawChange;
  const dragTickerRef = useRef<ReturnType<typeof createDragTicker> | null>(
    null,
  );
  if (!dragTickerRef.current) {
    dragTickerRef.current = createDragTicker(PAGE_PX);
  }

  const wrapDelta = (v: number) => {
    const n = count;
    let r = v % n;
    if (r > n / 2) r -= n;
    if (r < -n / 2) r += n;
    return r;
  };

  const layout = () => {
    const s = stateRef.current;
    const n = count;
    const radPer = (2 * Math.PI) / n;
    onYawChangeRef.current(s.pos * radPer);
    const R = getRadiusPx();
    const cards = cardRefs.current;
    cards.forEach((el, i) => {
      if (!el) return;
      const delta = wrapDelta(i - s.pos);
      const phi = delta * radPer;
      const depth = Math.cos(phi);
      if (depth <= -0.08) {
        el.style.display = 'none';
        return;
      }
      el.style.display = '';
      const f = Math.max(0, depth);
      el.style.transform = `translate(-50%,-50%) translateX(${
        Math.sin(phi) * R
      }px) perspective(600px) rotateY(${phi}rad) scale(${0.9 + 0.1 * f})`;
      el.style.filter = `blur(${(1 - f) * 5}px)`;
      el.style.opacity = String(Math.pow(f, 0.85));
      el.style.zIndex = String(Math.round(depth * 100));
    });
  };

  const stop = () => {
    const s = stateRef.current;
    if (s.raf) cancelAnimationFrame(s.raf);
    s.raf = null;
    s.last = null;
  };

  const cancelHint = () => {
    const s = stateRef.current;
    if (s.hintRaf) cancelAnimationFrame(s.hintRaf);
    s.hintRaf = null;
  };

  const centeredIndex = () => {
    const s = stateRef.current;
    return ((Math.round(s.pos) % count) + count) % count;
  };

  const normalize = () => {
    const s = stateRef.current;
    const w = s.pos - Math.floor(s.pos / count) * count;
    s.pos = w;
    s.target = w;
  };

  const set = (v: number, report = true) => {
    const s = stateRef.current;
    const prev = Math.round(s.pos);
    s.pos = v;
    layout();
    if (report && Math.round(v) !== prev) {
      hapSettle();
      onPageChangeRef.current(centeredIndex());
    }
  };

  const run = () => {
    stop();
    const s = stateRef.current;
    s.last = null;
    const step = (now: number) => {
      const dt = Math.min(1 / 30, s.last ? (now - s.last) / 1000 : 0);
      s.last = now;
      if (dt > 0) {
        const F = -SPRING_K * (s.pos - s.target) - SPRING_DAMP * s.vel;
        s.vel += F * dt;
        set(s.pos + s.vel * dt);
        if (Math.abs(s.pos - s.target) < 0.001 && Math.abs(s.vel) < 0.02) {
          s.vel = 0;
          set(s.target);
          normalize();
          stop();
          // Spring fully at rest, distinct from the per-page tick in set().
          hapSettle();
          return;
        }
      }
      s.raf = requestAnimationFrame(step);
    };
    s.raf = requestAnimationFrame(step);
  };

  const handle = useMemo<CarouselDriverHandle>(
    () => ({
      begin() {
        stop();
        cancelHint();
        hapGrab();
        dragTickerRef.current?.reset();
        const s = stateRef.current;
        s.userRotated = true;
        s.dragging = true;
        s.vel = 0;
        s.dragStart = s.pos;
      },
      move(dxFromStart: number) {
        dragTickerRef.current?.report(dxFromStart);
        const s = stateRef.current;
        set(s.dragStart - dxFromStart / PAGE_PX);
      },
      end(velocityPxPerSec: number) {
        const s = stateRef.current;
        s.dragging = false;
        const vp = -velocityPxPerSec / PAGE_PX;
        s.vel = vp;
        s.target = Math.round(s.pos + vp * FLING_SCALE);
        run();
      },
      nudge(dir: 1 | -1) {
        cancelHint();
        const s = stateRef.current;
        s.userRotated = true;
        s.dragging = false;
        s.vel = 0;
        s.target = Math.round(s.pos) + dir;
        run();
      },
      playHint() {
        const s = stateRef.current;
        if (s.userRotated || reducedMotion) return;
        const t0 = performance.now();
        const D = 1300;
        const base = s.pos;
        const tick = (now: number) => {
          if (stateRef.current.userRotated) return;
          const u = Math.min(1, (now - t0) / D);
          const np = base + 0.25 * Math.sin(Math.PI * u);
          set(np, false);
          if (u < 1) {
            stateRef.current.hintRaf = requestAnimationFrame(tick);
          } else {
            set(base, false);
          }
        };
        s.hintRaf = requestAnimationFrame(tick);
      },
      cancelHint,
      centeredIndex,
      hasUserRotated: () => stateRef.current.userRotated,
    }),
    [count, reducedMotion],
  );

  useEffect(() => {
    layout();
    const onResize = () => layout();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        cancelHint();
      }
    };
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
      cancelHint();
    };
  }, [count]);

  return handle;
}
